// API Client for Gallery Backend

// Use environment variable for API URL, fallback to relative path for development
const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

interface ApiError {
  error: string;
  message?: string;
  code?: string;
}

class ApiClient {
  private token: string | null = null;

  constructor() {
    // Load token from localStorage on init
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  private async request<T>(
    method: string,
    endpoint: string,
    body?: unknown
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await response.json();

    if (!response.ok) {
      const error = data as ApiError;
      throw new Error(error.message || error.error || 'Request failed');
    }

    return data as T;
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const result = await this.request<{ token: string; user: unknown }>('POST', '/auth/login', {
      email,
      password,
    });
    this.setToken(result.token);
    return result;
  }

  async register(email: string, password: string, name: string) {
    const result = await this.request<{ token: string; user: unknown }>('POST', '/auth/register', {
      email,
      password,
      name,
    });
    this.setToken(result.token);
    return result;
  }

  async logout() {
    this.setToken(null);
  }

  async getProfile() {
    return this.request<{ user: unknown }>('GET', '/auth/profile');
  }

  // Artist endpoints
  async getArtistProfile() {
    return this.request<{ artist: ArtistProfile }>('GET', '/artists/me');
  }

  async updateArtistProfile(data: Partial<ArtistProfile>) {
    return this.request<{ artist: ArtistProfile }>('PUT', '/artists/me', data);
  }

  async getStripeConnectStatus() {
    return this.request<StripeConnectStatus>('GET', '/artists/me/stripe/status');
  }

  async startStripeConnect() {
    return this.request<{ url: string; accountId: string }>('POST', '/artists/me/stripe/connect');
  }

  async refreshStripeOnboarding() {
    return this.request<{ url: string }>('POST', '/artists/me/stripe/refresh');
  }

  async getArtistEarnings() {
    return this.request<EarningsSummary>('GET', '/artists/me/earnings');
  }

  async getArtistPayouts() {
    return this.request<PayoutsResponse>('GET', '/artists/me/payouts');
  }

  // Gallery endpoints
  async getGalleries(params?: { page?: number; limit?: number; search?: string; sort?: string }): Promise<GalleriesResponse> {
    const searchParams = new URLSearchParams();
    const limit = params?.limit || 20;
    const page = params?.page || 1;
    const offset = (page - 1) * limit;

    searchParams.set('limit', String(limit));
    searchParams.set('offset', String(offset));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.sort) searchParams.set('sort', params.sort);

    const query = searchParams.toString();
    const response = await this.request<ApiGalleriesResponse>('GET', `/galleries${query ? `?${query}` : ''}`);

    // Transform API response to frontend expected format
    const galleries = (response.data || []).map((g) => ({
      ...g,
      // Add computed properties for backward compatibility
      isPublic: g.visibility === 'public',
      priceType: g.entryPriceCents > 0 ? 'one_time' as const : 'free' as const,
      priceCents: g.entryPriceCents,
      viewCount: g.viewCount || 0,
      tags: g.tags || [],
      coverImageUrl: g.coverImageUrl || null,
    }));

    return {
      galleries,
      total: response.pagination?.total || 0,
      page,
      limit,
    };
  }

  async getGallery(id: string) {
    return this.request<{ gallery: Gallery }>('GET', `/galleries/${id}`);
  }

  async getMyGalleries() {
    return this.request<{ galleries: Gallery[] }>('GET', '/artists/me/galleries');
  }

  async createGallery(data: CreateGalleryData) {
    return this.request<{ gallery: Gallery }>('POST', '/galleries', data);
  }

  async updateGallery(id: string, data: Partial<CreateGalleryData>) {
    return this.request<{ gallery: Gallery }>('PUT', `/galleries/${id}`, data);
  }

  async deleteGallery(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/galleries/${id}`);
  }

  // Artwork endpoints
  async getArtworks(galleryId: string) {
    return this.request<{ artworks: Artwork[] }>('GET', `/galleries/${galleryId}/artworks`);
  }

  async uploadArtwork(galleryId: string, file: File, metadata: ArtworkMetadata) {
    // Get upload URL first
    const { uploadUrl, assetId } = await this.request<{ uploadUrl: string; assetId: string }>(
      'POST',
      `/artworks/upload-url`,
      {
        galleryId,
        filename: file.name,
        contentType: file.type,
        ...metadata,
      }
    );

    // Upload to R2
    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    return { assetId };
  }

  // NFT/Wallet endpoints
  async getConnectedWallets() {
    return this.request<WalletsResponse>('GET', '/nfts/wallet');
  }

  async connectWallet(data: ConnectWalletData) {
    return this.request<ConnectWalletResponse>('POST', '/nfts/wallet/connect', data);
  }

  async disconnectWallet(chain: 'solana' | 'ethereum') {
    return this.request<{ success: boolean }>('DELETE', '/nfts/wallet/disconnect', { chain });
  }

  async mintNFT(data: MintNFTData) {
    return this.request<MintNFTResponse>('POST', '/nfts/mint', data);
  }

  async updateNFT(id: string, data: { txSignature?: string; status?: string }) {
    return this.request<{ success: boolean }>('PUT', `/nfts/${id}`, data);
  }

  async getNFTs(params?: { artworkId?: string; ownerWallet?: string; chain?: string; status?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.artworkId) searchParams.set('artworkId', params.artworkId);
    if (params?.ownerWallet) searchParams.set('ownerWallet', params.ownerWallet);
    if (params?.chain) searchParams.set('chain', params.chain);
    if (params?.status) searchParams.set('status', params.status);

    const query = searchParams.toString();
    return this.request<{ nfts: NFT[] }>('GET', `/nfts${query ? `?${query}` : ''}`);
  }

  async getNFT(id: string) {
    return this.request<NFTDetailsResponse>('GET', `/nfts/${id}`);
  }

  async getCollectorProfile(wallet: string) {
    return this.request<CollectorProfileResponse>('GET', `/nfts/collector/${wallet}`);
  }

  async getArtworkNFTs(artworkId: string) {
    return this.request<ArtworkNFTsResponse>('GET', `/nfts/artwork/${artworkId}`);
  }
}

// Types
export interface ArtistProfile {
  id: string;
  userId: string;
  displayName: string;
  bio: string | null;
  avatarUrl: string | null;
  stripeConnectId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StripeConnectStatus {
  connected: boolean;
  onboardingComplete: boolean;
  payoutsEnabled: boolean;
  chargesEnabled: boolean;
  accountId?: string;
  requiresInfo?: string[];
  defaultCurrency?: string;
  country?: string;
}

export interface EarningsSummary {
  totalEarnedCents: number;
  totalFeesCents: number;
  netEarningsCents: number;
  orderCount: number;
  recentOrders: RecentOrder[];
  stripeBalance: {
    available: { amount: number; currency: string }[];
    pending: { amount: number; currency: string }[];
  } | null;
}

export interface RecentOrder {
  id: string;
  amount: number;
  artistShareCents: number;
  platformFeeCents: number;
  galleryTitle: string;
  buyerEmail: string;
  createdAt: string;
}

export interface PayoutsResponse {
  payouts: Payout[];
}

export interface Payout {
  id: string;
  amount: number;
  currency: string;
  status: string;
  arrivalDate: number;
  created: number;
  method: string;
  type: string;
}

export interface Gallery {
  id: string;
  artistId: string;
  artistName?: string;
  title: string;
  description: string | null;
  coverImageUrl: string | null;
  visibility: 'public' | 'private' | 'unlisted';
  entryPriceCents: number;
  artworkCount?: number;
  tags: string[];
  viewCount: number;
  createdAt: string;
  updatedAt: string;
  // Computed properties for backward compatibility
  isPublic?: boolean;
  priceType?: 'free' | 'one_time' | 'subscription';
  priceCents?: number;
}

interface ApiGalleriesResponse {
  success: boolean;
  data: Gallery[];
  pagination: {
    total: number;
    offset: number;
    limit: number;
    hasMore: boolean;
  };
}

export interface GalleriesResponse {
  galleries: Gallery[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateGalleryData {
  title: string;
  description?: string;
  isPublic?: boolean;
  priceType?: 'free' | 'one_time' | 'subscription';
  priceCents?: number;
  tags?: string[];
}

export interface Artwork {
  id: string;
  galleryId: string;
  title: string;
  description: string | null;
  mediaUrl: string;
  mediaType: string;
  thumbnailUrl: string | null;
  displayOrder: number;
  duration: number | null;
  createdAt: string;
}

export interface ArtworkMetadata {
  title: string;
  description?: string;
  displayOrder?: number;
}

// NFT/Wallet Types
export interface WalletInfo {
  chain: 'solana' | 'ethereum';
  address: string;
}

export interface WalletsResponse {
  wallets: WalletInfo[];
}

export interface ConnectWalletData {
  chain: 'solana' | 'ethereum';
  walletAddress: string;
  message: string;
  signature: string;
  timestamp: number;
}

export interface ConnectWalletResponse {
  wallet: {
    chain: 'solana' | 'ethereum';
    address: string;
    verified: boolean;
  };
}

export interface MintNFTData {
  artworkId: string;
  chain: 'solana' | 'ethereum';
  editionTotal: number;
  editionNumber: number;
  recipientWallet?: string;
}

export interface MintNFTResponse {
  nft: {
    id: string;
    artworkId: string;
    chain: string;
    editionTotal: number;
    editionNumber: number;
    ownerWallet: string;
    status: string;
    createdAt: string;
  };
  mintingInfo: {
    standard: string;
    royaltyBasisPoints: number;
    artwork: {
      title: string;
      imageKey: string;
      cfImageId: string | null;
    };
  };
}

export interface NFT {
  id: string;
  artworkId: string;
  chain: string;
  mintAddress: string | null;
  editionTotal: number;
  editionNumber: number;
  metadataUri: string | null;
  ownerWallet: string | null;
  creatorWallet: string | null;
  royaltyBasisPoints: number;
  txSignature: string | null;
  status: string;
  mintedAt: string | null;
  createdAt: string;
  updatedAt: string;
  artwork: {
    title: string;
    previewKey: string | null;
    cfImageId: string | null;
  };
}

export interface NFTDetailsResponse {
  nft: NFT & {
    tokenId: string | null;
    contractAddress: string | null;
    metadataHash: string | null;
    explorerUrl: string | null;
  };
  artwork: {
    id: string;
    title: string;
    description: string | null;
    type: string;
    previewKey: string | null;
    cfImageId: string | null;
    width: number | null;
    height: number | null;
    yearCreated: number | null;
    medium: string | null;
  };
  gallery: {
    title: string;
  };
  artist: {
    name: string;
  };
}

export interface CollectorProfileResponse {
  wallet: string;
  totalOwned: number;
  nfts: {
    id: string;
    artworkId: string;
    chain: string;
    mintAddress: string | null;
    edition: string;
    mintedAt: string | null;
    artwork: {
      title: string;
      previewKey: string | null;
      cfImageId: string | null;
    };
    artist: {
      name: string;
    };
  }[];
}

export interface ArtworkNFTsResponse {
  artworkId: string;
  totalEditions: string;
  mintedCount: number;
  available: boolean;
  editions: {
    id: string;
    chain: string;
    mintAddress: string | null;
    edition: string;
    ownerWallet: string | null;
    creatorWallet: string | null;
    status: string;
    mintedAt: string | null;
  }[];
}

// Singleton instance
export const api = new ApiClient();
